import { useEffect, useId, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type ArchiveAgentDialogProps = {
  open: boolean;
  agentName: string;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (password: string) => void;
};

/** Archive keeps history. Confirm with the signed-in account password. */
export function ArchiveAgentDialog({
  open,
  agentName,
  busy = false,
  error = null,
  onCancel,
  onConfirm,
}: ArchiveAgentDialogProps) {
  const titleId = useId();
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPassword("");
    setLocalError(null);
  }, [open, agentName]);

  if (!open) return null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = password.trim();
    if (!value) {
      setLocalError("Enter your password to agree to archive this agent.");
      return;
    }
    if (value.length < 8) {
      setLocalError("Enter your ATW account password to confirm.");
      return;
    }
    setLocalError(null);
    onConfirm(value);
  };

  const message = localError || error;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        aria-label="Close archive confirmation"
        onClick={busy ? undefined : onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-[1] w-full max-w-md rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-white/10 dark:bg-[#1D232D]"
      >
        <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-white">
          Archive {agentName}?
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Archived agents keep their history and can still be opened from the Archive filter. They
          are not deleted. Enter your ATW account password to agree.
        </p>
        <form className="mt-4 space-y-4" onSubmit={submit}>
          <label className="block text-sm">
            <span className="font-medium text-gray-700 dark:text-gray-300">Password</span>
            <Input
              className="mt-1 w-full"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
              autoFocus
            />
          </label>
          {message ? (
            <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-100">
              {message}
            </p>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Archiving…" : "Archive agent"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
